import { useAppState, useAppDispatch } from '../../Shared/AppContext';

const DRAFT_VIEW_STATE = "draft";
const PENDING_APPROVAL_VIEW_STATE = "pending_approval";
const PENDING_REVISION_VIEW_STATE = "pending_revision";
const PUBLISHED_VIEW_STATE = "published";
const DENIED_VIEW_STATE = "denied";

export const VIEW_STATES = [
  DRAFT_VIEW_STATE,
  PENDING_APPROVAL_VIEW_STATE,
  PENDING_REVISION_VIEW_STATE,
  PUBLISHED_VIEW_STATE,
  DENIED_VIEW_STATE,
];

export function useYourDiscourseState() {
  return useAppState();
}

export function useYourDiscourseActions() {
  const dispatch = useAppDispatch();

  function initializeState() {
    dispatch((appState) => ({
      ...appState,
      loading: true,
      documentsByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, []])),
      pageNumberByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, 0])),
      moreDocumentsAvailableByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, true])),
      viewState: PUBLISHED_VIEW_STATE,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  function onResponse(response) {
    dispatch((appState) => {
      const viewState = response.viewState || appState.viewState;

      const newDocumentsByViewState = {
        ...appState.documentsByViewState
      };
      const existingDocuments = appState.documentsByViewState?.[viewState] || [];
      // Set for O(1) lookups when deduplicating against existing documents
      // Set.has() is O(1) lookup vs Array.includes() which is O(n), so for a feed with many documents the filtering is faster
      const existingIds = new Set(existingDocuments.map(d => d.id));
      const uniqueNewDocuments = (response.documents || []).filter(d => !existingIds.has(d.id));
      newDocumentsByViewState[viewState] = existingDocuments.concat(uniqueNewDocuments);

      const newPageNumberByViewState = {
        ...appState.pageNumberByViewState
      };
      newPageNumberByViewState[viewState] = response.pageNumber || (appState.pageNumberByViewState?.[viewState] || 0);

      const newMoreDocumentsAvailableByViewState = {
        ...appState.moreDocumentsAvailableByViewState
      };
      newMoreDocumentsAvailableByViewState[viewState] =
        response.moreDocumentsAvailable !== undefined
          ? response.moreDocumentsAvailable
          : (appState.moreDocumentsAvailableByViewState?.[viewState] ?? true);

      return {
        ...appState,
        documentsByViewState: newDocumentsByViewState,
        pageNumberByViewState: newPageNumberByViewState,
        moreDocumentsAvailableByViewState: newMoreDocumentsAvailableByViewState,
        feedbackMessage: response.feedbackMessage || null,
        feedbackVariant: response.feedbackVariant || null,
        loading: false,
      };
    });
  }

  function onError(feedback) {
    dispatch((appState) => ({
      ...appState,
      ...feedback,
      loading: false,
    }));
  }

  function setViewState(viewState) {
    dispatch((appState) => ({
      ...appState,
      viewState: viewState,
      loading: true,
    }));
  }

  function getFeedbackFromLocalStorage(feedbackMessage, feedbackVariant) {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: feedbackMessage,
      feedbackVariant: feedbackVariant,
    }));
  }

  function clearFeedback() {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  return {
    initializeState,
    onResponse,
    onError,
    setViewState,
    getFeedbackFromLocalStorage,
    clearFeedback,
  };
}
